import { Box, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { formatPrice } from 'modules/billing/lib/f25Prices'
import type PlanVariant from 'modules/upsell/lib/selfUpsell/plans/PlanVariant'
import { PeriodUnit, UpsellDiscount } from 'modules/upsell/lib/selfUpsell/types'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type PriceInfo = {
  variant: PlanVariant | undefined
  period: PeriodUnit
  currency: string
  seats: number
  currentSeats: number
  discount: UpsellDiscount | undefined
  fromCrossSelling?: boolean
}

const PriceInfo = ({
  variant,
  period,
  currency,
  seats,
  currentSeats,
  discount,
  fromCrossSelling = false,
}: PriceInfo) => {
  const { isExpenses } = useSelfUpsellContext()
  const perSeatPrice = variant?.pricing.getSeatsPrice(period)
  const originalPerSeatPrice = variant?.pricing.getOriginalSeatsPrice(period)
  const feePrice =
    period === 'year'
      ? variant?.pricing.getAnnualFeePrice(period)
      : variant?.pricing.getFeePrice(period)
  const originalFeePrice = variant?.pricing.getOriginalFeePrice(period)
  const stairstepPrice = variant?.pricing.getStairstepPrice(period, currentSeats)
  const tieredPrice = variant?.pricing.getTieredPrice(period, seats)
  // The first tier of a plan have a price per seat of 0. We can take the endingUnit of this tier to figure out the starting seats.
  const includedSeatsPerModule = tieredPrice?.tiers?.[0]?.endingUnit
  const totalPrice = variant?.pricing.getTotalPrice(period, seats)
  // Helper function to check if discount is available
  const hasDiscount = discount && discount.percentage > 0
  // Helper function to calculate discounted price
  const calculateDiscountedPrice = (originalPrice: number) => {
    if (!hasDiscount) return originalPrice
    return originalPrice * (1 - discount.percentage / 100)
  }

  // Helper function to render price with optional discount or original price strikethrough
  const renderPrice = (
    currentPrice: number,
    label: string,
    crossSelling: boolean,
    origPrice?: number | undefined
  ) => (
    <Box
      flexDirection={crossSelling ? 'column' : 'row'}
      gap="s4"
      alignItems={crossSelling ? 'flexEnd' : 'center'}
    >
      {hasDiscount ? (
        <Box flexDirection="row" gap="s2" alignItems="center">
          <Text size="200" color="grey700" stroked>
            {formatPrice(currentPrice, currency)}
          </Text>
          <Text size="200" color="grey1000">
            {formatPrice(calculateDiscountedPrice(currentPrice), currency)}
          </Text>
        </Box>
      ) : origPrice && origPrice > currentPrice ? (
        <Box flexDirection="row" gap="s2" alignItems="center">
          <Text size="200" color="grey700" stroked>
            {formatPrice(origPrice, currency)}
          </Text>
          <Text size="200" color="grey1000">
            {formatPrice(currentPrice, currency)}
          </Text>
        </Box>
      ) : (
        <Text size="200" color="grey1000">
          {formatPrice(currentPrice, currency)}
        </Text>
      )}
      <Text size="200" color="grey700">
        {label}
      </Text>
    </Box>
  )

  if (!variant) return null

  if (isExpenses()) {
    return (
      <>
        {seats > currentSeats ? (
          <Box flexDirection="row" gap="s4">
            {'-'}
          </Box>
        ) : (
          <Box flexDirection="row" gap="s4">
            {hasDiscount ? (
              <Box flexDirection="row" gap="s2" alignItems="center">
                <Text size="200" color="grey700" stroked>
                  {formatPrice(totalPrice || 0, currency)}
                </Text>
                <Text size="200" color="grey1000">
                  {`${formatPrice(calculateDiscountedPrice(totalPrice || 0), currency)}`}
                </Text>
              </Box>
            ) : (
              <Text size="200" color="grey700" stroked>
                {formatPrice(totalPrice || 0, currency)}
              </Text>
            )}

            <Text size="200" color="grey700">
              {i18n.t('self_upsell.first_step.expenses_seats_included', {
                seats: includedSeatsPerModule,
              })}{' '}
              {seats - (includedSeatsPerModule || 0) > 0
                ? i18n.t('self_upsell.first_step.expenses_extra_seats', {
                    seats: seats - (includedSeatsPerModule || 0),
                  })
                : null}
            </Text>
          </Box>
        )}
      </>
    )
  }
  return (
    <Box flexDirection="row" gap="s4">
      {perSeatPrice && perSeatPrice > 0
        ? renderPrice(
            perSeatPrice,
            i18n.t('self_upsell.first_step.seats_month'),
            fromCrossSelling,
            originalPerSeatPrice
          )
        : null}
      {feePrice && feePrice > 0 ? (
        <Box flexDirection="row" gap="s4" alignItems="center">
          {hasDiscount ? (
            <Box flexDirection="row" gap="s2" alignItems="center">
              <Text size="200" color="grey700" stroked>
                {formatPrice(feePrice, currency)}
              </Text>
              <Text size="200" color="grey1000">
                {formatPrice(calculateDiscountedPrice(feePrice), currency)}
              </Text>
            </Box>
          ) : originalFeePrice && originalFeePrice > feePrice ? (
            <Box flexDirection="row" gap="s2" alignItems="center">
              <Text size="200" color="grey700" stroked>
                {formatPrice(originalFeePrice, currency)}
              </Text>
              <Text size="200" color="grey1000">
                {formatPrice(feePrice, currency)}
              </Text>
            </Box>
          ) : (
            <Text size="200" color="grey1000">
              {renderPrice(feePrice, i18n.t('pricing_plans.fixed_fee'), fromCrossSelling)}
            </Text>
          )}
        </Box>
      ) : null}
      {stairstepPrice && stairstepPrice > 0
        ? renderPrice(stairstepPrice, i18n.t('pricing_plans.fixed_fee'), fromCrossSelling)
        : null}
      {tieredPrice && tieredPrice.selectedPrice > 0
        ? renderPrice(
            tieredPrice.selectedPrice,
            i18n.t('pricing_plans.fixed_fee'),
            fromCrossSelling
          )
        : null}
    </Box>
  )
}

export default PriceInfo
